import { Calendar as CalendarIcon } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './DateField.module.scss';

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const MONTH_OPTIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
].map((label, index) => ({ label, value: `${index + 1}`.padStart(2, '0') }));

const EMPTY_DATE_PARTS = { month: '', day: '', year: '' };

const onlyDigits = (text, maxLength) => `${text || ''}`.replace(/\D/g, '').slice(0, maxLength);

export const datePartsToIso = ({ month, day, year }) => {
  if (!month || !day || `${year}`.length !== 4) return null;

  const paddedDay = `${day}`.padStart(2, '0');
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  const isRealDate =
    parsed.getFullYear() === Number(year) &&
    parsed.getMonth() === Number(month) - 1 &&
    parsed.getDate() === Number(day);
  return isRealDate ? `${year}-${month}-${paddedDay}` : null;
};

export const isoToDateParts = (value) => {
  const match = ISO_DATE_PATTERN.exec(`${value || ''}`.trim());
  if (!match) return EMPTY_DATE_PARTS;

  const parts = { month: match[2], day: match[3], year: match[1] };
  return datePartsToIso(parts) ? parts : EMPTY_DATE_PARTS;
};

const DateField = ({ id, label, value, onChange, errorText, warningText, min, max }) => {
  const [parts, setParts] = React.useState(() => isoToDateParts(value));
  const pickerRef = React.useRef(null);

  React.useEffect(() => {
    setParts(isoToDateParts(value));
  }, [value]);

  const openPicker = () => {
    const picker = pickerRef.current;
    if (!picker) return;
    try {
      if (typeof picker.showPicker === 'function') {
        picker.showPicker();
        return;
      }
    } catch {
      // Browser refused the programmatic picker; fall back to focusing the input.
    }
    picker.focus();
  };

  const update = (part, partValue) => {
    const nextParts = { ...parts, [part]: partValue };
    setParts(nextParts);
    onChange(datePartsToIso(nextParts) || '');
  };

  const stateClass = (errorText && styles.inputError) || (warningText && styles.inputWarning) || '';

  return (
    <label className={styles.field} htmlFor={id}>
      {label ? <span className={styles.label}>{label}</span> : null}
      <div className={styles.parts}>
        <select
          id={id}
          className={stateClass}
          value={parts.month}
          onChange={(event) => update('month', event.target.value)}
        >
          <option value="">Month</option>
          {MONTH_OPTIONS.map((month) => (
            <option key={month.value} value={month.value}>
              {month.label}
            </option>
          ))}
        </select>
        <input
          id={`${id}-day`}
          type="text"
          inputMode="numeric"
          placeholder="Day"
          maxLength={2}
          aria-label={`${label || 'Date'} day`}
          className={stateClass}
          value={parts.day}
          onChange={(event) => update('day', onlyDigits(event.target.value, 2))}
        />
        <input
          id={`${id}-year`}
          type="text"
          inputMode="numeric"
          placeholder="Year"
          maxLength={4}
          aria-label={`${label || 'Date'} year`}
          className={stateClass}
          value={parts.year}
          onChange={(event) => update('year', onlyDigits(event.target.value, 4))}
        />
        <span className={styles.calendar}>
          <button
            type="button"
            className={styles.calendarButton}
            aria-label={`Pick ${label || 'date'} from a calendar`}
            onClick={openPicker}
          >
            <CalendarIcon size={15} strokeWidth={1.8} aria-hidden="true" />
          </button>
          <input
            ref={pickerRef}
            type="date"
            tabIndex={-1}
            aria-hidden="true"
            min={min}
            max={max}
            className={styles.calendarInput}
            value={value || ''}
            onChange={(event) => onChange(event.target.value)}
          />
        </span>
      </div>
      {errorText ? <em className={styles.error}>{errorText}</em> : null}
      {!errorText && warningText ? <em className={styles.warning}>{warningText}</em> : null}
    </label>
  );
};

DateField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  errorText: PropTypes.string,
  warningText: PropTypes.string,
  min: PropTypes.string,
  max: PropTypes.string,
};

export default DateField;
